import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Card,
  SxProps,
  Theme,
  useTheme,
  Tooltip as MuiTooltip,
} from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AnalyticsGraphResponse } from "../../../models/Dashboard/Analytics";
import { LabelWiseInventoryOpsDetails } from "../../../models/Dashboard/OpsDashboard/OpsAnalytics";
import { useEffect, useRef, useState } from "react";

type BarChartLayout = "vertical" | "horizontal";

export interface AppStackChartKeyConfig {
  key: string;
  color: string;
  activeColor: string;
}

export function parseAnalyticsGraphDataToAppStackChartData(
  response: AnalyticsGraphResponse[]
): LabelWiseInventoryOpsDetails[] {
  if (!response || response.length == 0) {
    return [
      {
        label: "",
        raw_inward_tat: 0,
        raw_inward_tat_qc: 0,
        print_outward_tat: 0,
        print_inward_tat: 0,
        print_inward_tat_qc: 0,
      },
    ];
  }

  const weekMap: Record<string, LabelWiseInventoryOpsDetails> = {};

  const labelMap: Record<string, keyof LabelWiseInventoryOpsDetails> = {
    "Raw Inward Tat": "raw_inward_tat",
    "Raw Inward QC Tat": "raw_inward_tat_qc",
    "Print Outward Tat": "print_outward_tat",
    "Print Inward Tat": "print_inward_tat",
    "Print Inward QC Tat": "print_inward_tat_qc",
  };

  for (const entry of response) {
    const fieldKey = labelMap[entry.label];
    if (!fieldKey) continue;

    for (const val of entry.values) {
      const weekLabel = val.label;
      const value =
        typeof val.value === "string" ? parseFloat(val.value) || 0 : val.value;

      if (!weekMap[weekLabel]) {
        weekMap[weekLabel] = {
          label: weekLabel,
          raw_inward_tat: 0,
          raw_inward_tat_qc: 0,
          print_outward_tat: 0,
          print_inward_tat: 0,
          print_inward_tat_qc: 0,
        };
      }

      (weekMap[weekLabel] as any)[fieldKey] = value;
    }
  }

  return Object.values(weekMap).map((entry) => ({
    label: entry.label ?? "",
    raw_inward_tat: entry.raw_inward_tat ?? 0,
    raw_inward_tat_qc: entry.raw_inward_tat_qc ?? 0,
    print_outward_tat: entry.print_outward_tat ?? 0,
    print_inward_tat: entry.print_inward_tat ?? 0,
    print_inward_tat_qc: entry.print_inward_tat_qc ?? 0,
  }));
}

export interface AppStackChartProps {
  title: string;
  message?: string;
  sx?: SxProps<Theme>;
  data: any[];
  data_keys_config: AppStackChartKeyConfig[];
  layout: BarChartLayout;
  barCategoryGap?: number;
  barSize?: number;
  barGap?: number;
  fontSize?: string;
  legendFontSize?: string;
  scrollable?: boolean;
  maxVisibleItems?: number;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper + "CC",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          padding: 1,
          boxShadow: theme.shadows[2],
        }}
      >
        <CustomTypography variant="body2" fontWeight={600}>
          {label}
        </CustomTypography>
        {payload.map((entry: any, index: number) => (
          <Box
            key={`tooltip-${index}`}
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: 0.5,
              color: entry.color,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: entry.color,
                marginRight: 1,
              }}
            />
            <CustomTypography variant="body2">
              {entry.name}: {entry.value}
            </CustomTypography>
          </Box>
        ))}
      </Box>
    );
  }

  return null;
};

export default function AppStackChart({
  title,
  message,
  sx,
  layout,
  data,
  data_keys_config,
  barSize = 15,
  barCategoryGap,
  barGap,
  fontSize = "0.8rem",
  scrollable = true,
}: AppStackChartProps) {
  function getLayout(layout: BarChartLayout): BarChartLayout {
    return layout === "horizontal" ? "vertical" : "horizontal";
  }

  const chartHeight = Math.max(data.length * 60, 350);
  const isVertical = layout === "vertical";
  const shouldScrollHorizontally = isVertical && data.length > 6;

  const containerWidth = shouldScrollHorizontally
    ? data.length * (barSize + 50)
    : "100%";

  return (
    <Card sx={sx}>
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <CustomTypography sx={{ pb: 4 }} variant="heading4">
          {title}
        </CustomTypography>
        {message && (
          <MuiTooltip title={message}>
            <InfoOutlinedIcon sx={{ cursor: "pointer" }} />
          </MuiTooltip>
        )}
      </Box>
      {data.length !== 0 ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            marginTop: 20,
          }}
        >
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              overflowX: shouldScrollHorizontally ? "auto" : "hidden",
              paddingRight: "10px",
              maxHeight: 600,
            }}
          >
            <div style={{ width: containerWidth }}>
              <ResponsiveContainer
                width="100%"
                height={scrollable ? chartHeight : 400}
                minHeight={415}
              >
                <BarChart
                  data={data}
                  layout={getLayout(layout)}
                  barSize={barSize}
                  barGap={barGap}
                  barCategoryGap={barCategoryGap}
                >
                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{
                      paddingBottom: "1rem",
                      fontSize: "0.8rem",
                    }}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  {layout === "horizontal" ? (
                    <>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={false}
                        vertical={true}
                      />
                      <YAxis
                        dataKey="label"
                        type="category"
                        width={100}
                        fontSize="0.7rem"
                        fontWeight={600}
                      />
                      <XAxis type="number" fontSize="0.7rem" fontWeight={600} />
                    </>
                  ) : (
                    <>
                      <XAxis
                        dataKey="label"
                        type="category"
                        fontSize="0.7rem"
                        fontWeight={600}
                      />
                      <YAxis type="number" fontSize="0.7rem" fontWeight={600} />
                    </>
                  )}
                  {data_keys_config.map((key_config) => (
                    <Bar
                      key={key_config.key}
                      dataKey={key_config.key}
                      stackId="a"
                      fill={key_config.color}
                      name={key_config.key}
                      activeBar={
                        <Rectangle
                          fill={key_config.activeColor}
                          stroke={key_config.color}
                        />
                      }
                      radius={isVertical ? [2, 2, 0, 0] : [0, 2, 2, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "80%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CustomTypography variant="heading8" color="textSecondary">
            Nothing to show for the selected filters
          </CustomTypography>
        </Box>
      )}
    </Card>
  );
}
